import clsx from 'clsx';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import FadeTransition from './components/css-transitions/FadeTransition';
import { isExistFunction } from '../../../utils';

const defineEventForCloseBtnAndLastFocusableElement = (closeBtnRef, lastFocusableElement) => {
  if (lastFocusableElement && closeBtnRef.current) {
    lastFocusableElement.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        closeBtnRef.current.focus();
      }
    });

    // shift tab from close button to last focusable element
    closeBtnRef.current.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        lastFocusableElement.focus();
      }
    });
  }
};

export default function Popup({ id, controlElement, children, showPopup, onHide, onOpen, setShowPopup, overlayBackground, popupClass, popupSizeClass = 'max-w-500', btnClass = '', popupWrapperClass = 'p-12', closeOnOverlay = true, closeBtnCustomAttributes = {}}) {
  const [haveEle, setHaveEle] = useState(false);
  // state detect popup is already open or not
  const [localShowPopup, setLocalShowPopup] = useState(false);
  const closeBtnRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    const $modalSection = document.querySelector('#modal-root');
    if (!$modalSection) {
      const $body = document.querySelector('body');
      const $modal = document.createElement('div');
      $modal.id = 'modal-root';
      $modal.classList.add('z-9999');
      $modal.classList.add('relative');
      $body.appendChild($modal);
    }
    setHaveEle(true);
  }, []);

  const callFunc = (func, val) => {
    if (isExistFunction(func)) {
      func(val);
    }
  };

  const onClosePopup = useCallback(() => {
    callFunc(onHide);
    callFunc(setShowPopup, false);
  }, [onHide, setShowPopup]);

  const handleCloseClick = () => {
    onClosePopup();
    // focus back to control element when popup is closed
    if (controlElement) {
      controlElement.focus();
    }
  };

  useEffect(() => {
    if (showPopup) {
      callFunc(onOpen);
    } else {
      onClosePopup();
    }
    setLocalShowPopup(showPopup);
  }, [onClosePopup, onHide, onOpen, showPopup]);

  // Focus on close button when open popup & focus on last focusable element when close button is focused
  useEffect(() => {
    const getLastFocusableElement = (container) => {
      const focusableElements = container.querySelectorAll('a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])');
      return focusableElements[focusableElements.length - 1];
    };
    if (popupRef.current && localShowPopup) {
      const lastFocusableElement = getLastFocusableElement(popupRef.current);
      closeBtnRef.current.focus();
      defineEventForCloseBtnAndLastFocusableElement(closeBtnRef, lastFocusableElement);
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && localShowPopup && !document.querySelector('.tooltip-estimate')) {
        onClosePopup();
        // focus back to control element when popup is closed
        if (controlElement) {
          controlElement.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [localShowPopup, onClosePopup, controlElement]);

  if (!haveEle) {
    return null;
  };

  return createPortal(
    <div id={id}>
      <FadeTransition in={showPopup} unMountOnExit duration={400}>
        <div className={popupClass}>
          <div
            className={clsx('modal-overlay fixed top-0 left-0 bg-black w-full h-full z-9999 opacity-70', overlayBackground)}
            aria-hidden
            onClick={closeOnOverlay ? handleCloseClick : null}
          />
          <div ref={popupRef} className={clsx(
            'config-modal-wrapper',
            'fixed transform-center-middle max-h-full w-full items-center justify-center flex z-9999 p-12 flex-col',
            popupSizeClass,
          )} >
            <div className="absolute inset-0 bottom-auto z-20">
              <button ref={closeBtnRef} type="button"
                onClick={onClosePopup}
                aria-controls={id || 'modal-root'}
                className={clsx('modal-close-btn', 'absolute top-20 right-20 close-btn z-20', btnClass, !btnClass.includes('hover:') && 'hover:text-primary-100')}
                data-event-trigger={closeBtnCustomAttributes['data-event-trigger']}
                data-event-type={closeBtnCustomAttributes['data-event-type']}
                data-event-module={closeBtnCustomAttributes['data-event-module']}
                data-event-label={closeBtnCustomAttributes['data-event-label']}
                data-event-category={closeBtnCustomAttributes['data-event-category']}
                data-event-page={closeBtnCustomAttributes['data-event-page']}
              >
                <span className="icomoon icon-close text-font-28" aria-hidden="true" />
                <span className="sr-only">Close Modal Popup</span>
              </button>
            </div>
            <div className={clsx('config-modal-inner relative bg-white flex-col w-full rounded-4 overflow-hidden overflow-y-auto')}>
              <div className={clsx(popupWrapperClass)}>
                {children}
              </div>
            </div>
          </div>
        </div>
      </FadeTransition>

    </div>
    ,
    document.querySelector('#modal-root'),
  );
};
